import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  changePassword,
  getLoginLogs,
  resetError,
} from "../../features/slices/UserSlice";

const PASSWORD_FIELDS = [
  { name: "currentPassword", label: "Current Password" },
  { name: "newPassword", label: "New Password" },
  { name: "confirmPassword", label: "Confirm Password" },
];

const EMPTY_FORM = { currentPassword: "", newPassword: "", confirmPassword: "" };

const formatLoginTime = (timestamp) =>
  timestamp ? moment(timestamp).format("DD/MM/YYYY, hh:mm:ss A") : "-";

const formatLocation = (item) =>
  [item.location, item.ip].filter(Boolean).join(" / ") || "-";

export default function Security() {
  const [updatePassword, setUpdatePassword] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [visible, setVisible] = useState({});
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetError());
    dispatch(getLoginLogs());
  }, []);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(""), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  const openForm = () => {
    setForm(EMPTY_FORM);
    setVisible({});
    setError("");
    setMessage("");
    setUpdatePassword(true);
  };

  const closeForm = () => {
    setUpdatePassword(false);
    setError("");
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const toggleVisible = (name) =>
    setVisible({ ...visible, [name]: !visible[name] });

  const submitPassword = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = form;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please Enter All Passwords");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must contain 8 characters");
      return;
    }
    if (confirmPassword !== newPassword) {
      setError("Password Should Match");
      return;
    }
    if (newPassword === currentPassword) {
      setError("New password must be different from the current password");
      return;
    }
    setSubmitting(true);
    try {
      const res = await dispatch(changePassword(form)).unwrap();
      setUpdatePassword(false);
      setForm(EMPTY_FORM);
      setMessage(res?.message || "Password Updated Successfully!");
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="security-sm">
      <h6 className="mt-3 security-title">Security</h6>
      {updatePassword ? (
        <form
          className="mt-4 update-password-container"
          onSubmit={submitPassword}
          noValidate
        >
          {error ? (
            <p className="text--medium" style={{ color: "red" }}>
              <i className="bi bi-exclamation-octagon"></i> {error}
            </p>
          ) : null}
          <div className="row">
            <div className="col-xl-7 col-lg-9 col-md-12">
              {PASSWORD_FIELDS.map(({ name, label }) => (
                <div key={name}>
                  <label className="text--medium" htmlFor={name}>
                    {label}
                  </label>
                  <div className="password mb-4 mt-2">
                    <input
                      type={visible[name] ? "text" : "password"}
                      name={name}
                      id={name}
                      value={form[name]}
                      autoComplete={
                        name === "currentPassword"
                          ? "current-password"
                          : "new-password"
                      }
                      onChange={handleChange}
                    />
                    <i
                      className={`bi ${
                        visible[name] ? "bi-eye-slash" : "bi-eye"
                      }`}
                      role="button"
                      aria-label={visible[name] ? "Hide password" : "Show password"}
                      onClick={() => toggleVisible(name)}
                    ></i>
                  </div>
                </div>
              ))}
              <div className="d-flex justify-content-between mt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="border-btn"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="standard-btn"
                  disabled={submitting}
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div>
          <div className="row">
            <div className="col-xl-3 col-lg-3 col-md-4 pt-3">
              <p
                className="text-light text--medium"
                style={{ fontWeight: "200" }}
              >
                Password
              </p>
              <button
                onClick={openForm}
                className="standard-btn change-password mb-5"
              >
                Change Password
              </button>
            </div>
            {message ? (
              <div className="col-xl-4 col-lg-4 col-md-4 pt-4">
                <p
                  className="text--medium mb-0 password-updated-message"
                  style={{ color: "yellowgreen", marginTop: "30px" }}
                >
                  {message}
                </p>
              </div>
            ) : null}
          </div>
          <h6 className="security-title">Login History</h6>
          {!user.loginLogs?.length ? (
            <p className="text--normal mt-3">No Logs Found!</p>
          ) : (
            <div className="login-history-parent">
              <table className="table login-logs-table">
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    <th>Browser</th>
                    <th>Location/IP</th>
                  </tr>
                </thead>
                <tbody>
                  {user.loginLogs.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{formatLoginTime(item.timestamp)}</td>
                      <td>{item.browser || "-"}</td>
                      <td>{formatLocation(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
